import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import {
  loadClientNotifications,
  loadProviderNotifications,
  type AppNotification,
} from '@/lib/inAppNotifications';
import { supabase } from '@/lib/supabase';
import { useUserId } from './AuthProvider';
import Spinner from './Spinner';
import styles from './NotificationBell.module.css';

type Props = {
  role: 'client' | 'provider';
  bellColor?: string;
  /** Which edge the dropdown is anchored to. Use "left" inside the sidebar so
   *  the panel opens toward the content instead of off-screen. */
  align?: 'left' | 'right';
};

export default function NotificationBell({ role, bellColor = 'var(--ink)', align = 'right' }: Props) {
  const userId = useUserId();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const dismissedRef = useRef<Set<string>>(new Set());
  const wrapperRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    if (!userId) {
      setNotifications([]);
      return;
    }
    try {
      const rows =
        role === 'client'
          ? await loadClientNotifications(userId)
          : await loadProviderNotifications(userId);

      const { data: dismissedRows } = await supabase
        .from('user_notification_dismissals')
        .select('notification_id')
        .eq('user_id', userId)
        .eq('role', role);

      const dismissed = new Set<string>([
        ...(dismissedRows ?? []).map((row: any) => row.notification_id as string),
        ...dismissedRef.current,
      ]);
      dismissedRef.current = dismissed;

      setNotifications(rows.filter((item) => !dismissed.has(item.id)));
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications([]);
    }
  }, [role, userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const dismiss = async (id: string) => {
    const removed = notifications.find((item) => item.id === id);
    dismissedRef.current.add(id);
    setNotifications((current) => current.filter((item) => item.id !== id));

    if (!userId) return;

    const { error } = await supabase.from('user_notification_dismissals').upsert(
      { user_id: userId, role, notification_id: id },
      { onConflict: 'user_id,role,notification_id' }
    );

    if (error) {
      console.error('Error dismissing notification:', error);
      dismissedRef.current.delete(id);
      if (removed) setNotifications((current) => [removed, ...current]);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setLoading(true);
      await refresh();
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.button}
        onClick={() => void toggle()}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={
          notifications.length > 0
            ? `Notifications (${notifications.length} non lues)`
            : 'Notifications'
        }
      >
        <Bell size={24} strokeWidth={2.2} color={bellColor} />
        {notifications.length > 0 ? <span className={styles.dot} /> : null}
      </button>

      {open ? (
        <div
          className={`${styles.dropdown} ${align === 'left' ? styles.dropdownLeft : ''}`}
          role="dialog"
          aria-label="Notifications"
        >
          <div className={styles.header}>
            <h2 className={styles.title}>Notifications</h2>
            <button
              type="button"
              className={styles.close}
              onClick={() => setOpen(false)}
              aria-label="Fermer"
            >
              <X size={16} />
            </button>
          </div>

          {loading ? (
            <div className={styles.loading}>
              <Spinner />
            </div>
          ) : notifications.length === 0 ? (
            <p className={styles.empty}>Aucune notification pour le moment.</p>
          ) : (
            <ul className={styles.list}>
              {notifications.map((item) => (
                <li key={item.id} className={styles.item}>
                  <span className={`${styles.typeDot} ${styles[item.type]}`} />
                  {item.href ? (
                    <Link
                      className={`${styles.text} ${styles.textLink}`}
                      to={item.href}
                      onClick={() => setOpen(false)}
                    >
                      {item.text}
                    </Link>
                  ) : (
                    <span className={styles.text}>{item.text}</span>
                  )}
                  <button
                    type="button"
                    className={styles.itemClose}
                    onClick={() => void dismiss(item.id)}
                    aria-label="Ignorer cette notification"
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
